import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../api/client';
import type { DryRunResult, PolicyDiff, PolicyDraft, PolicyHistoryEntry, PolicyRule, PolicyVersion } from '../../api/types';
import { useAuth } from '../../auth/AuthProvider';
import { useShell } from '../../lib/shell';
import { errorText } from '../../lib/useRelay';

export const HISTORY_LIMIT = 20;

export const STALE_DRAFT = 'The draft changed elsewhere, reloaded. Reapply your edit if it is still needed.';

export interface UsePolicy {
  active: PolicyVersion | null;
  draft: PolicyDraft | null;
  diff: PolicyDiff | null;
  history: PolicyHistoryEntry[] | null;
  /** The draft compiled for the relay (POST /policy/dry-run), refreshed after every save. */
  compiled: DryRunResult | null;
  loading: boolean;
  /** A load or action failure. */
  error: string | null;
  /** Something the admin should know that is not a failure (a stale draft was reloaded). */
  notice: string | null;
  busy: 'save' | 'publish' | 'dryRun' | null;
  /** PUT the whole rule list with the ETag last read. False when it did not land. */
  saveRules: (rules: PolicyRule[]) => Promise<boolean>;
  publish: () => Promise<boolean>;
  dryRun: () => Promise<void>;
  dismissNotice: () => void;
}

const isStale = (e: unknown) => e instanceof ApiError && (e.code === 'precondition_failed' || e.statusCode === 412);

/**
 * The policy the Firewall screen edits: the active version, the draft (with its ETag), the diff
 * between them, the history and the compiled draft. Saves and publishes carry the ETag; on 412
 * the draft is reloaded rather than overwritten. Every change also refreshes the shell's
 * unpublished-changes count, so the sidebar badge follows.
 */
export function usePolicy(enabled: boolean): UsePolicy {
  const { api } = useAuth();
  const { reloadDiff } = useShell();
  const [active, setActive] = useState<PolicyVersion | null>(null);
  const [draft, setDraft] = useState<PolicyDraft | null>(null);
  const [etag, setEtag] = useState<string | null>(null);
  const [diff, setDiff] = useState<PolicyDiff | null>(null);
  const [history, setHistory] = useState<PolicyHistoryEntry[] | null>(null);
  const [compiled, setCompiled] = useState<DryRunResult | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<UsePolicy['busy']>(null);

  const loadDraft = useCallback(async () => {
    const [d, df, c] = await Promise.all([api.getDraft(), api.getDiff(), api.dryRun()]);
    setDraft(d.draft);
    setEtag(d.etag);
    setDiff(df);
    setCompiled(c);
  }, [api]);

  const loadAll = useCallback(async () => {
    const [a, h] = await Promise.all([api.getPolicy(), api.getHistory(HISTORY_LIMIT), loadDraft()]);
    setActive(a);
    setHistory(h);
  }, [api, loadDraft]);

  useEffect(() => {
    if (!enabled) return;
    let live = true;
    setLoading(true);
    loadAll()
      .then(() => live && setError(null))
      .catch((e: unknown) => live && setError(errorText(e)))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [enabled, loadAll]);

  /** Runs an action; a 412 reloads the draft and becomes a notice instead of an error. */
  const act = useCallback(
    async (kind: NonNullable<UsePolicy['busy']>, run: () => Promise<void>): Promise<boolean> => {
      setBusy(kind);
      setError(null);
      try {
        await run();
        return true;
      } catch (e) {
        if (isStale(e)) {
          setNotice(STALE_DRAFT);
          await loadDraft().catch((re: unknown) => setError(errorText(re)));
        } else {
          setError(errorText(e));
        }
        return false;
      } finally {
        setBusy(null);
        if (kind !== 'dryRun') void reloadDiff();
      }
    },
    [loadDraft, reloadDiff],
  );

  const saveRules = useCallback(
    (rules: PolicyRule[]) =>
      act('save', async () => {
        if (etag === null) throw new Error('The draft is not loaded yet.');
        const saved = await api.putDraft(rules, etag);
        setDraft(saved.draft);
        setEtag(saved.etag);
        setNotice(null);
        const [df, c] = await Promise.all([api.getDiff(), api.dryRun()]);
        setDiff(df);
        setCompiled(c);
      }),
    [act, api, etag],
  );

  const publish = useCallback(
    () =>
      act('publish', async () => {
        if (etag === null) throw new Error('The draft is not loaded yet.');
        setActive(await api.publish(etag));
        setNotice(null);
        await loadAll();
      }),
    [act, api, etag, loadAll],
  );

  const dryRun = useCallback(async () => {
    await act('dryRun', async () => setCompiled(await api.dryRun()));
  }, [act, api]);

  const dismissNotice = useCallback(() => setNotice(null), []);

  return { active, draft, diff, history, compiled, loading, error, notice, busy, saveRules, publish, dryRun, dismissNotice };
}
